import { create } from 'zustand';
import type { QuickTaskProposal } from '../types';

interface AiQuickCreateState {
  /** The `projectId:boardId` the current instruction and proposal belong to. */
  scope: string | null;
  instruction: string;
  proposal: QuickTaskProposal | null;
  proposeErrorKey: string | null;
  applyErrorKey: string | null;
}

interface AiQuickCreateActions {
  /** Drops everything when the panel opens on a different board. */
  enterScope: (scope: string) => void;
  setInstruction: (instruction: string) => void;
  setProposal: (proposal: QuickTaskProposal | null) => void;
  setProposeErrorKey: (key: string | null) => void;
  setApplyErrorKey: (key: string | null) => void;
  clearErrors: () => void;
  /** Empties the draft but keeps the scope, so the panel stays usable for the next task. */
  clearDraft: () => void;
  reset: () => void;
}

type AiQuickCreateStore = AiQuickCreateState & AiQuickCreateActions;

const initialState: AiQuickCreateState = {
  scope: null,
  instruction: '',
  proposal: null,
  proposeErrorKey: null,
  applyErrorKey: null,
};

/**
 * Session-scoped store for the AI quick-create panel.
 * Persists instruction and proposal across open/close cycles
 * within the same page session. Resets on page reload.
 *
 * Scoped to one board. A proposal names a status, a group and an assignee that only exist
 * on the board it was generated for, so carrying it to another board offered the user a
 * ready-to-confirm proposal built from another board's ids.
 */
export const useAiQuickCreateStore = create<AiQuickCreateStore>()((set) => ({
  ...initialState,

  enterScope: (scope) =>
    set((state) => (state.scope === scope ? state : { ...initialState, scope })),

  setInstruction: (instruction) => set({ instruction }),

  setProposal: (proposal) => set({ proposal }),

  setProposeErrorKey: (key) => set({ proposeErrorKey: key }),

  setApplyErrorKey: (key) => set({ applyErrorKey: key }),

  clearErrors: () => set({ proposeErrorKey: null, applyErrorKey: null }),

  clearDraft: () => set((state) => ({ ...initialState, scope: state.scope })),

  reset: () => set(initialState),
}));
